import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { PlainBadge, SeverityBadge } from '@/components/forge/StatusBadge'
import { Num, Serial } from '@/components/forge/primitives'
import { formatDateTime } from '@/lib/format'

/**
 * One entry in the feed. LIVE.
 *
 * ── There is no byline, and that is not an omission here ─────────────────
 * The demo card led with an avatar, a name and a role. `LogBookEntryOut` serves
 * none of them: `created_by` is a real column, set from the token on every
 * write, and it is simply not on the response. So the card shows what the API
 * knows — the serial, when it was written, which shift and which asset — and
 * says nothing about who. Printing a name this client cannot have would be the
 * one failure a logbook must not have. Recorded in BACKEND-GAPS.md.
 *
 * ── The violet "AI Analyzed" badge is gone with it ───────────────────────
 * It meant "an investigation cited this note", which the demo computed by
 * running Forge AI's own `searchLogbook` tool call backwards over the fixture.
 * The API has no such field. What it does have is `anomaly_id` — the anomaly
 * the WRITER linked, which is a different and weaker claim, and is rendered as
 * a plain link rather than borrowing the violet. The Violet Reservation is for
 * machine authorship; a person typing an id is not that.
 *
 * ── The two counts, not the things they count ────────────────────────────
 * A list row carries `photo_count` and `sensor_tag_count` and not the photos or
 * the windows themselves — those are on the detail read, at
 * `/app/logbook/:entryId`. Shown as counts, which is all this row was told.
 *
 * ── The whole card opens the entry ───────────────────────────────────────
 * Through the title's link, stretched over the card with `after:inset-0`, not
 * by wrapping the card in a `Link`: the machine, anomaly and work-order links
 * inside it would be nested interactive elements, which is invalid markup. They
 * sit above the stretch on `z-10` and keep their own destinations.
 */
export function LogbookEntry({ entry }) {
  const { t } = useTranslation(['app', 'common'])

  /* `hierarchy` is NAMES ONLY — `hierarchy_names` serves no ids — so this is
     printed and not linked. The parent ids are on the row, but they belong to
     three different tiers and only `machine_id` has a destination in this app;
     guessing wrong sends a reader to a 404. */
  const place = [entry.hierarchy.machine, entry.hierarchy.line, entry.hierarchy.plant]
    .filter(Boolean)
    .join(' · ')

  return (
    <Panel interactive className="relative flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <SeverityBadge severity={entry.severity} />
        <PlainBadge>{t(`logbook.category.${entry.category}`)}</PlainBadge>

        <h3 className="m-0 min-w-0 flex-1 text-body font-semibold text-ink">
          <Link
            to={`/app/logbook/${entry.id}`}
            aria-label={t('logbook.entry.open', { serial: entry.serialNumber })}
            className="text-ink no-underline after:absolute after:inset-0 after:content-[''] hover:text-on-forge"
          >
            {entry.title}
          </Link>
        </h3>

        <Serial value={entry.serialNumber} className="text-micro text-ink-dim" />
      </div>

      <p className="m-0 text-body-sm text-ink-dim">
        <Num>
          {/* The SHIFT DATE, not `created_at`. They differ by design: a night
              shift written up at 00:40 belongs to the day it started, and the
              date the writer chose is the one the log is read by. */}
          {formatDateTime(entry.shiftDate, { dateStyle: 'medium' })}
        </Num>
        {place ? <> · {place}</> : null}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {entry.machineId ? (
          <Link
            to={`/app/assets/${entry.machineId}`}
            aria-label={t('logbook.entry.openAsset', { name: entry.hierarchy.machine ?? '' })}
            className="relative z-10 flex items-center gap-1.5 rounded-xs bg-sunken px-2 py-1 text-micro font-semibold text-ink-dim no-underline transition-colors hover:text-ink"
          >
            <Icon name="machine" size="xs" />
            {entry.hierarchy.machine}
          </Link>
        ) : null}

        {entry.photoCount > 0 ? (
          <span className="flex items-center gap-1.5 rounded-xs bg-sunken px-2 py-1 text-micro text-ink-dim">
            <Icon name="eye" size="xs" />
            <Num>{t('logbook.entry.photos', { count: entry.photoCount })}</Num>
          </span>
        ) : null}

        {entry.sensorTagCount > 0 ? (
          <span className="flex items-center gap-1.5 rounded-xs bg-sunken px-2 py-1 text-micro text-ink-dim">
            <Icon name="wave" size="xs" />
            <Num>{t('logbook.entry.sensorTags', { count: entry.sensorTagCount })}</Num>
          </span>
        ) : null}

        {entry.anomalyId ? (
          <Link
            to={`/app/anomalies/${entry.anomalyId}`}
            className="relative z-10 ms-auto flex items-center gap-1.5 rounded-xs px-2 py-1 text-micro font-semibold text-on-forge no-underline transition-colors hover:bg-sunken"
          >
            <Icon name="bolt" size="xs" />
            {t('logbook.entry.linkedAnomaly')}
            <Icon name="right" size="xs" className="mirror-rtl" />
          </Link>
        ) : null}

        {entry.workOrderId ? (
          <Link
            to={`/app/work-orders?wo=${entry.workOrderId}`}
            className={`relative z-10 flex items-center gap-1.5 rounded-xs px-2 py-1 text-micro font-semibold text-on-forge no-underline transition-colors hover:bg-sunken ${entry.anomalyId ? '' : 'ms-auto'}`}
          >
            <Icon name="wrench" size="xs" />
            {t('logbook.entry.linkedWorkOrder')}
            <Icon name="right" size="xs" className="mirror-rtl" />
          </Link>
        ) : null}
      </div>

      {/*
        ── The DEMO card's prose body, and why it is not here ──────────────
        The demo list sent `text` on every row, so the feed read as prose. The
        live collection sends `title` and the two counts and stops: `observation`
        is on `LogBookEntryOut` alone, which is the detail read. Fetching it per
        card would be fifty requests to render one page.

        So the feed shows titles, which is why `title` is a required field on the
        form with a hint asking for a real one. The body is on the entry's own
        screen. The demo's tag chips have no column at all and are gone for good.

        {entry.tags.map((tag) => (
          <Mono key={tag} className="rounded-xs bg-sunken px-2 py-1 text-micro text-ink-dim">
            #{tag}
          </Mono>
        ))}
      */}
    </Panel>
  )
}
